export interface User {
  id: string;
  email: string;
  created_at: string;
}

export interface Generation {
  id: string;
  original_prompt: string;
  enhanced_prompt: string;
  negative_prompt: string;
  seed: number | null;
  num_inference_steps: number;
  width: number;
  height: number;
  upstream_job_id: string | null;
  worker_id: string | null;
  status: "queued" | "running" | "completed" | "failed" | string;
  error: string | null;
  download_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface QueueStatus {
  workers?: Array<{
    worker_id: string;
    queued: number;
    running: number;
    completed: number;
    failed: number;
    load: number;
    available: boolean;
  }>;
  totals?: {
    queued: number;
    running: number;
    completed: number;
    failed: number;
    load: number;
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Request failed");
  }
  return payload as T;
}

export const panoApi = {
  register(email: string, password: string) {
    return request<{ user: User }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  login(email: string, password: string) {
    return request<{ user: User }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  logout() {
    return request<{ ok: boolean }>("/auth/logout", { method: "POST" });
  },

  me() {
    return request<{ user: User }>("/auth/me");
  },

  generate(payload: {
    prompt: string;
    negative_prompt?: string;
    seed?: number;
    num_inference_steps?: number;
    width?: number;
    height?: number;
  }) {
    return request<{ generation: Generation }>("/generate", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  generations() {
    return request<{ generations: Generation[] }>("/generations");
  },

  generation(id: string) {
    return request<{ generation: Generation }>(`/generations/${id}`);
  },

  queue() {
    return request<QueueStatus>("/queue");
  },
};
